import React, { useRef, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { MENU, MenuItem, activeKey } from './menu';

const SoonPill: React.FC = () => (
  <span className="rounded-full px-2 py-1 text-xs font-medium bg-slate-100 text-slate-400">
    준비 중
  </span>
);

/** 이 항목(또는 자식)이 지금 열려 있는 화면인가 */
function isActive(item: MenuItem, active: string | undefined): boolean {
  if (item.children) {
    return item.children.some((c) => c.key === active);
  }
  return item.key === active;
}

/* folded — 넓은 화면에서 접힌 상태. 아이콘만 두고 이름은 title 로(admin 사이드바와 같다). 좁은 화면 ☰ 서랍은 늘 펼친다 */
const Row: React.FC<{ item: MenuItem; active: string | undefined; folded: boolean }> = ({ item, active, folded }) => {
  const on = isActive(item, active);
  const hide = folded ? 'md:hidden' : '';

  return (
    <>
      <Link
        to={item.path}
        aria-current={on ? 'page' : undefined}
        title={folded ? item.label : undefined}
        className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-colors ${folded ? 'md:justify-center' : ''} ${
          on
            ? 'bg-indigo-50 text-indigo-600 font-medium'
            : 'text-slate-500 font-medium hover:bg-slate-50 hover:text-slate-700'
        }`}
      >
        <span className="w-4 flex-shrink-0 flex items-center justify-center">
          <item.icon size={16} aria-hidden="true" />
        </span>
        <span className={`flex-1 whitespace-nowrap ${hide}`}>{item.label}</span>
        {item.soon && !item.children && <span className={hide}><SoonPill /></span>}
      </Link>

      {/* 자식은 부모가 열려 있을 때만 편다 — 시안과 같다 */}
      {item.children && on && (
        <div className={`flex flex-col gap-px mt-0.5 mb-1 ${hide}`}>
          {item.children.map((child) => {
            const sub = child.key === active;
            return (
              <Link
                key={child.key}
                to={child.path}
                aria-current={sub ? 'page' : undefined}
                className={`flex items-center gap-2 pl-9 pr-2.5 py-1.5 rounded-lg text-sm transition-colors ${
                  sub
                    ? 'bg-indigo-50/60 text-indigo-600 font-medium'
                    : 'text-slate-500 font-medium hover:bg-slate-50 hover:text-slate-700'
                }`}
              >
                <span className="flex-1 whitespace-nowrap">{child.label}</span>
                {child.soon && <SoonPill />}
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
};

const Sidebar: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const active = activeKey(pathname);
  const { user, logout } = useAuth();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  /* 좁은 화면(md 미만)에서는 사이드바를 접고 ☰ 로 연다 — 232px 고정이라 본문이 찌그러졌다(#113) */
  const [navOpen, setNavOpen] = useState(false);
  /* 넓은 화면에서 사이드바 접기 — 가장자리 버튼(admin 과 같다) */
  const [folded, setFolded] = useState(false);
  const hide = folded ? 'md:hidden' : '';
  useEffect(() => { setNavOpen(false); }, [pathname]);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };
    if (isProfileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileOpen]);

  return (
    <div className="flex h-screen bg-slate-100">
      {navOpen && <div className="fixed inset-0 z-30 bg-black/30 md:hidden" onClick={() => setNavOpen(false)} />}
      <aside className={`w-[232px] ${folded ? 'md:w-16' : ''} flex-shrink-0 bg-white border-r border-slate-200 flex flex-col fixed inset-y-0 left-0 z-40 transform transition-all md:relative md:translate-x-0 ${navOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <button
          type="button"
          onClick={() => setFolded(!folded)}
          aria-label={folded ? '메뉴 펼치기' : '메뉴 접기'}
          title={folded ? '메뉴 펼치기' : '메뉴 접기'}
          className="hidden md:block absolute top-1/2 -translate-y-1/2 -right-3 z-50 p-1 rounded-full bg-white border border-slate-200 hover:bg-slate-50 shadow-md"
        >
          {folded ? <ChevronRight size={14} aria-hidden="true" /> : <ChevronLeft size={14} aria-hidden="true" />}
        </button>
        {/* 로고 */}
        <div className={`px-4 pt-5 pb-4 ${folded ? 'md:px-2' : ''}`}>
          <Link to="/" className="flex flex-col items-center gap-1.5 group">
            <img
              src="/GRK_partners_세로형.jpg"
              alt="GRK Partners"
              className={`${folded ? 'md:h-10' : ''} h-14 w-auto object-contain group-hover:scale-105 transition-transform`}
              style={{ mixBlendMode: 'multiply' }}
            />
            <div className={`text-xs tracking-widest text-slate-400 font-medium ${hide}`}>
              PROJECT
            </div>
          </Link>
        </div>

        {/* 메뉴 */}
        <nav aria-label="주 메뉴" className={`flex-1 px-3 overflow-y-auto ${folded ? 'md:px-2' : ''}`}>
          <div className="flex flex-col gap-0.5">
            {MENU.map((entry) =>
              'group' in entry ? (
                <div
                  key={entry.group}
                  className="px-2.5 pt-3.5 pb-1.5 text-xs font-semibold tracking-wider text-slate-400"
                >
                  {/* 접히면 머리글 대신 가는 선 */}
                  <span className={hide}>{entry.group}</span>
                  {folded && <div className="hidden md:block border-t border-slate-200" />}
                </div>
              ) : (
                <Row key={entry.key} item={entry} active={active} folded={folded} />
              ),
            )}
          </div>
        </nav>

        {/* 프로필 — 모양은 GRK_admin 사이드바와 같다(아바타 40px · 이름 text-sm). 누르면 로그아웃 */}
        <div ref={profileRef} className={`relative px-3 py-4 ${folded ? 'md:px-2' : ''}`}>
          <button
            type="button"
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            aria-expanded={isProfileOpen}
            title={folded ? user?.name : undefined}
            className={`group flex items-center gap-3 w-full px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors ${folded ? 'md:px-0 md:justify-center' : ''}`}
          >
            <div
              className={`h-10 w-10 rounded-full border-2 bg-gray-200 flex items-center justify-center flex-shrink-0 transition-colors group-hover:border-indigo-300 ${
                isProfileOpen ? 'border-indigo-300' : 'border-transparent'
              }`}
            >
              <span className="text-sm font-semibold text-gray-500">{user?.name?.charAt(0)?.toUpperCase() || '?'}</span>
            </div>
            <div className={`flex-1 min-w-0 text-left ${hide}`}>
              <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
              <p className="text-xs text-gray-500 truncate">{user?.role}</p>
            </div>
          </button>

          {isProfileOpen && (
            <div className={`absolute bottom-full left-3 right-3 mb-1 bg-white border border-slate-200 rounded-lg shadow-lg p-1 z-50 ${folded ? 'md:right-auto md:w-40' : ''}`}>
              <button
                type="button"
                onClick={logout}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
                로그아웃
              </button>
            </div>
          )}
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-auto">
        <button
          onClick={() => setNavOpen(true)}
          aria-label="메뉴 열기"
          className="md:hidden sticky top-0 z-20 m-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 shadow-sm"
        >☰ 메뉴</button>
        {children}
      </main>
    </div>
  );
};

export { Sidebar };
